import { Mic, Pause, Play, Square } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOptionalRecording } from '@/pages/audio-ai/RecordingContext';
import { formatDurationMS } from '@/utils/date';

interface Props {
  /** Fecha o drawer mobile depois que a gravação inicia. */
  onCloseMobile?: () => void;
  /** Quando true, mostra versão compacta (só ícone) no desktop. */
  isDesktopCollapsed?: boolean;
}

/**
 * Botão fixo na sidebar (acima do bloco do usuário) para iniciar a
 * gravação de áudio do atendimento. Só fica ativo quando o médico está
 * dentro da ficha de um paciente (`/pacientes/:id`); fora disso, mostra
 * estado desabilitado com tooltip explicando o motivo. Quando há
 * gravação em andamento, transforma-se em um controle compacto (tempo +
 * pausa + parar) — o pill flutuante continua visível em paralelo no
 * canto inferior esquerdo da tela.
 */
export const SidebarRecordingButton = ({ onCloseMobile, isDesktopCollapsed = false }: Props) => {
  const ctx = useOptionalRecording();
  const { pathname } = useLocation();
  const { user } = useAuth();

  // Provider está montado em Shell; ctx só seria null se o componente
  // for usado fora dele.
  if (!ctx) return null;

  const { isRecording, isPaused, recordingTime, start, stop, pauseResume } = ctx;

  const match = pathname.match(/^\/pacientes\/([^/?]+)/);
  const currentPatientId = match?.[1] ?? null;
  const canRecord = !!currentPatientId && !!user?.uid;

  // ── Estado: gravando ──────────────────────────────────────────────
  if (isRecording) {
    // Versão colapsada: dot pulsante + botão de parar, sem timer/label.
    // Time fica visível pelo pill flutuante e pelo tooltip aqui.
    if (isDesktopCollapsed) {
      return (
        <div
          className="hidden md:flex mx-2 mb-2 px-1.5 py-2 rounded-lg bg-error/10 border border-error/30 flex-col items-center gap-1.5"
          title={`${isPaused ? 'Pausado' : 'Gravando'} · ${formatDurationMS(recordingTime)}`}
        >
          <span
            className={`w-2 h-2 rounded-full bg-error ${!isPaused ? 'animate-pulse' : ''}`}
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={pauseResume}
            title={isPaused ? 'Retomar' : 'Pausar'}
            className="w-7 h-7 rounded-full hover:bg-surface-container flex items-center justify-center text-on-surface-variant transition-colors"
          >
            {isPaused ? <Play size={13} /> : <Pause size={13} />}
          </button>
          <button
            type="button"
            onClick={stop}
            title="Parar gravação"
            className="w-7 h-7 rounded-full bg-error/15 hover:bg-error/25 flex items-center justify-center text-error transition-colors"
          >
            <Square size={11} className="fill-current" />
          </button>
        </div>
      );
    }
    return (
      <div className="px-3 py-2.5 mx-1 mb-2 rounded-lg bg-error/10 border border-error/30 flex items-center gap-2">
        <span
          className={`w-2 h-2 rounded-full bg-error shrink-0 ${!isPaused ? 'animate-pulse' : ''}`}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-error/80 leading-none">
            {isPaused ? 'Pausado' : 'Gravando'}
          </div>
          <div className="font-mono text-sm font-bold text-on-surface tabular-nums leading-tight">
            {formatDurationMS(recordingTime)}
          </div>
        </div>
        <button
          type="button"
          onClick={pauseResume}
          title={isPaused ? 'Retomar' : 'Pausar'}
          className="w-7 h-7 rounded-full hover:bg-surface-container flex items-center justify-center text-on-surface-variant transition-colors shrink-0"
        >
          {isPaused ? <Play size={13} /> : <Pause size={13} />}
        </button>
        <button
          type="button"
          onClick={stop}
          title="Parar gravação"
          className="w-7 h-7 rounded-full bg-error/15 hover:bg-error/25 flex items-center justify-center text-error transition-colors shrink-0"
        >
          <Square size={11} className="fill-current" />
        </button>
      </div>
    );
  }

  // ── Estado: parado, fora da ficha do paciente ─────────────────────
  if (!canRecord) {
    return (
      <button
        type="button"
        disabled
        title="Abra a ficha de um paciente para gravar"
        className={`mb-2 rounded-lg flex items-center gap-3 text-on-surface-variant/50 border border-dashed border-outline-variant/20 cursor-not-allowed ${
          isDesktopCollapsed
            ? 'md:mx-2 md:w-[calc(100%-1rem)] md:justify-center md:px-0 md:py-2 mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
            : 'mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
        }`}
      >
        <Mic size={16} className="shrink-0" />
        <span className={`text-sm flex-1 text-left truncate ${isDesktopCollapsed ? 'md:hidden' : ''}`}>Gravar áudio</span>
        <span className={`text-[10px] uppercase tracking-wider text-on-surface-variant/40 shrink-0 ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
          Abra paciente
        </span>
      </button>
    );
  }

  // ── Estado: parado, dentro da ficha → habilitado ──────────────────
  return (
    <button
      type="button"
      onClick={() => {
        void start(currentPatientId, user.uid);
        onCloseMobile?.();
      }}
      title="Iniciar gravação para este paciente"
      className={`mb-2 rounded-lg flex items-center gap-3 bg-error/10 hover:bg-error/15 text-error border border-error/30 transition-colors font-medium ${
        isDesktopCollapsed
          ? 'md:mx-2 md:w-[calc(100%-1rem)] md:justify-center md:px-0 md:py-2 mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
          : 'mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
      }`}
    >
      <Mic size={16} className="shrink-0" />
      <span className={`text-sm flex-1 text-left ${isDesktopCollapsed ? 'md:hidden' : ''}`}>Gravar áudio</span>
    </button>
  );
};
